<template>
  <div>
    <Manpowermenubar_govem />

    <v-container fluid class="pa-4 pa-md-6 app-bg">
      <!-- ===== Header Card ===== -->
      <v-card class="mb-5 elevation-2 rounded-xl header-card">
        <v-card-title class="d-flex align-center py-4 px-6 header-title-bar">
          <div>
            <h1 class="text-h5 text-md-h4 font-weight-bold white--text">
              <v-icon left color="white" large>mdi-finance</v-icon>
              ระบบบริหารจัดการและติดตามงบประมาณพนักงานราชการ
            </h1>
            <div class="white--text text--lighten-2 mt-1 ml-10 text-body-2">
              ปีงบประมาณ {{ filters.report_year }} | งวดที่
              {{ filters.report_time }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <div class="text-right">
            <v-btn
              color="white"
              outlined
              class="mr-2"
              @click="exportCurrentView"
              :loading="isExporting"
            >
              <v-icon left>mdi-microsoft-excel</v-icon> ส่งออก Excel
            </v-btn>
          </div>
        </v-card-title>

        <!-- ===== Filter Section (Redesigned) ===== -->
        <v-card-text class="px-6 py-5 filter-section">
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-subtitle-1 font-weight-bold primary--text"
              >ตัวกรองข้อมูล</span
            >
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              color="grey darken-1"
              @click="resetFilters"
              class="text-capitalize"
            >
              <v-icon small left>mdi-refresh</v-icon>
              ล้างตัวกรอง
            </v-btn>
          </div>

          <!-- Row 1: ปี, งวด, กลุ่มสถานศึกษา -->
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.report_year"
                :items="yearOptions"
                label="ปีงบประมาณ"
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-calendar"
                class="filter-input"
                @change="fetchData"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.report_time"
                :items="timeOptions"
                label="งวดการเบิกจ่าย"
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-clock-time-four-outline"
                class="filter-input"
                @change="fetchData"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.selectedUserStatuses"
                :items="userStatusOptions"
                item-text="text"
                item-value="value"
                label="กลุ่มสถานศึกษา"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-domain"
                placeholder="ทุกกลุ่ม"
                class="filter-input"
              >
                <template v-slot:prepend-item>
                  <v-list-item
                    ripple
                    @mousedown.prevent
                    @click="toggleAllUserStatuses"
                  >
                    <v-list-item-action>
                      <v-icon
                        :color="
                          filters.selectedUserStatuses.length > 0
                            ? 'primary'
                            : ''
                        "
                      >
                        {{ selectAllUserStatusIcon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>เลือกทั้งหมด</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 2"
                    small
                    close
                    color="deep-purple lighten-4"
                    text-color="deep-purple darken-3"
                    @click:close="removeUserStatus(item.value)"
                  >
                    <v-icon left x-small>{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </v-chip>
                  <span v-if="index === 2" class="grey--text text-caption ml-1">
                    (+{{ filters.selectedUserStatuses.length - 2 }} อื่นๆ)
                  </span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filters.selectedRegions"
                :items="regionOptions"
                item-text="region_name"
                item-value="region_ID"
                label="ภาค"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-map"
                placeholder="ทุกภาค"
                class="filter-input"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 2"
                    small
                    close
                    color="blue lighten-4"
                    text-color="blue darken-3"
                    @click:close="removeRegion(item.region_ID)"
                  >
                    {{ item.region_name }}
                  </v-chip>
                  <span v-if="index === 2" class="grey--text text-caption ml-1">
                    (+{{ filters.selectedRegions.length - 2 }} อื่นๆ)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-autocomplete
                v-model="filters.selectedProvinces"
                :items="availableProvinces"
                item-text="province_name"
                item-value="province_ID"
                label="จังหวัด"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-map-marker"
                placeholder="ทุกจังหวัด"
                class="filter-input"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 1"
                    small
                    close
                    color="teal lighten-4"
                    text-color="teal darken-3"
                    @click:close="removeProvince(item.province_ID)"
                  >
                    {{ item.province_name }}
                  </v-chip>
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ filters.selectedProvinces.length - 1 }} อื่นๆ)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Active Filters Summary -->
          <v-slide-y-transition>
            <div
              v-if="hasActiveFilters"
              class="mt-3 d-flex align-center flex-wrap"
            >
              <v-icon small color="orange darken-2" class="mr-1"
                >mdi-filter-check</v-icon
              >
              <span
                class="text-caption orange--text text--darken-2 font-weight-medium mr-2"
              >
                ตัวกรองที่ใช้งาน:
              </span>
              <v-chip
                v-for="af in activeFilterChips"
                :key="af.key"
                x-small
                close
                class="mr-1 mb-1"
                color="orange lighten-4"
                text-color="orange darken-3"
                @click:close="clearFilter(af.key)"
              >
                {{ af.label }}
              </v-chip>
              <v-chip
                x-small
                outlined
                color="grey"
                class="ml-1 mb-1"
                @click="resetFilters"
              >
                ล้างทั้งหมด
              </v-chip>
            </div>
          </v-slide-y-transition>
        </v-card-text>
      </v-card>

      <!-- ===== Summary Cards (Redesigned) ===== -->
      <v-row class="mb-5">
        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--colleges"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--blue">
                  <v-icon color="white" size="28">mdi-school</v-icon>
                </div>
                <v-chip
                  x-small
                  :color="
                    collegeStats.reportedPercentage >= 80
                      ? 'success'
                      : 'warning'
                  "
                  dark
                >
                  {{ collegeStats.reportedPercentage }}% รายงาน
                </v-chip>
              </div>
              <div class="text-h4 font-weight-black mb-1">
                {{ filteredColleges.length }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                สถานศึกษาทั้งหมด
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="d-flex text-caption">
                <span class="success--text font-weight-bold mr-3">
                  <v-icon x-small color="success">mdi-check-circle</v-icon>
                  {{ collegeStats.reported }} รายงานแล้ว
                </span>
                <span class="error--text font-weight-bold">
                  <v-icon x-small color="error">mdi-alert-circle</v-icon>
                  {{ collegeStats.unreported }} ยังไม่รายงาน
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--allocated"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--green">
                  <v-icon color="white" size="28">mdi-cash-plus</v-icon>
                </div>
                <span class="text-caption grey--text">งบจัดสรร</span>
              </div>
              <div class="text-h5 font-weight-black mb-1">
                {{ formatCurrencyShort(summaryData.total_allocated) }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                {{ formatCurrency(summaryData.total_allocated) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--disbursed"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--orange">
                  <v-icon color="white" size="28">mdi-cash-check</v-icon>
                </div>
                <v-progress-circular
                  :value="summaryData.overall_disburse_percentage"
                  :color="
                    getPercentageColor(summaryData.overall_disburse_percentage)
                  "
                  :size="42"
                  :width="5"
                  :rotate="-90"
                >
                  <span class="text-caption font-weight-bold"
                    >{{ summaryData.overall_disburse_percentage }}%</span
                  >
                </v-progress-circular>
              </div>
              <div class="text-h5 font-weight-black mb-1">
                {{ formatCurrencyShort(summaryData.total_disbursed) }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                เบิกจ่ายแล้ว
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--pending"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--cyan">
                  <v-icon color="white" size="28">mdi-clipboard-clock</v-icon>
                </div>
                <span class="text-caption grey--text">รอดำเนินการ</span>
              </div>
              <div class="text-h4 font-weight-black mb-1">
                {{ stats.submitted + stats.checking }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                รอการตรวจสอบ
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="d-flex text-caption">
                <span class="success--text font-weight-bold mr-3">
                  <v-icon x-small color="success">mdi-check</v-icon>
                  {{ stats.approved }} อนุมัติ
                </span>
                <span class="error--text font-weight-bold">
                  <v-icon x-small color="error">mdi-close</v-icon>
                  {{ stats.rejected }} ปฏิเสธ
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Quick Stats: Group by user_status ===== -->
      <v-card
        class="rounded-xl elevation-1 mb-5"
        v-if="userStatusSummary.length > 0"
      >
        <v-card-title class="py-3 px-5">
          <v-icon left color="deep-purple" small>mdi-domain</v-icon>
          <span class="text-subtitle-2 font-weight-bold"
            >สรุปตามกลุ่มสถานศึกษา</span
          >
        </v-card-title>
        <v-card-text class="pt-0 px-5 pb-4">
          <v-row dense>
            <v-col
              v-for="grp in userStatusSummary"
              :key="grp.code"
              cols="6"
              sm="4"
              md="2"
            >
              <v-card
                outlined
                class="rounded-lg pa-3 text-center group-stat-card"
                :class="{
                  'group-stat-card--active': filters.selectedUserStatuses.includes(
                    grp.code
                  )
                }"
                @click="toggleUserStatusFilter(grp.code)"
              >
                <v-icon :color="grp.color" size="20" class="mb-1">{{
                  grp.icon
                }}</v-icon>
                <div
                  class="text-h6 font-weight-black"
                  :class="grp.color + '--text'"
                >
                  {{ grp.total }}
                </div>
                <div
                  class="text-caption grey--text text--darken-1"
                  style="line-height: 1.2"
                >
                  {{ grp.label }}
                </div>
                <div class="text-caption mt-1">
                  <span class="success--text">{{ grp.reported }}</span> /
                  <span class="error--text">{{ grp.unreported }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ===== Tabs ===== -->
      <v-card class="rounded-xl elevation-2 overflow-hidden">
        <v-tabs
          v-model="activeTab"
          background-color="white"
          color="primary"
          grow
          show-arrows
          class="border-bottom"
        >
          <v-tab>
            <v-icon left small>mdi-school</v-icon>
            <span class="d-none d-sm-inline">1. ติดตามสถานศึกษา</span>
            <span class="d-inline d-sm-none">ติดตาม</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-file-check-outline</v-icon>
            <span class="d-none d-sm-inline">2. ตรวจสอบสถานะ</span>
            <span class="d-inline d-sm-none">สถานะ</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-chart-bar</v-icon>
            <span class="d-none d-sm-inline">3. สถิติเบิกจ่าย</span>
            <span class="d-inline d-sm-none">สถิติ</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-trending-up</v-icon>
            <span class="d-none d-sm-inline">4. พยากรณ์</span>
            <span class="d-inline d-sm-none">พยากรณ์</span>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- ===== TAB 1: ติดตามสถานศึกษา ===== -->
          <v-tab-item>
            <v-container fluid class="pa-4">
              <!-- สรุปข้อมูลด้านบน -->
              <v-row class="mb-4">
                <v-col cols="6" md="3">
                  <v-card outlined class="rounded-lg pa-4 text-center">
                    <div class="text-h3 font-weight-bold primary--text">
                      {{ filteredColleges.length }}
                    </div>
                    <div class="text-caption grey--text">สถานศึกษาทั้งหมด</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined class="rounded-lg pa-4 text-center">
                    <div class="text-h3 font-weight-bold success--text">
                      {{ collegeStats.reported }}
                    </div>
                    <div class="text-caption grey--text">รายงานแล้ว</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined class="rounded-lg pa-4 text-center">
                    <div class="text-h3 font-weight-bold error--text">
                      {{ collegeStats.unreported }}
                    </div>
                    <div class="text-caption grey--text">ยังไม่รายงาน</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined class="rounded-lg pa-4 text-center">
                    <v-progress-circular
                      :rotate="-90"
                      :size="80"
                      :width="10"
                      :value="collegeStats.reportedPercentage"
                      :color="
                        getPercentageColor(collegeStats.reportedPercentage)
                      "
                    >
                      <span class="text-h6 font-weight-bold"
                        >{{ collegeStats.reportedPercentage }}%</span
                      >
                    </v-progress-circular>
                    <div class="text-caption grey--text mt-2">
                      อัตราการรายงาน
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- กราฟ สัดส่วนภาค/จังหวัด -->
              <v-row class="mb-4">
                <v-col cols="12" md="5">
                  <v-card outlined class="rounded-lg pa-4">
                    <h3 class="text-subtitle-1 font-weight-bold mb-2">
                      สัดส่วนการรายงานแยกตามสถานะ
                    </h3>
                    <apexchart
                      type="donut"
                      height="280"
                      :options="chartOptions.collegeStatusPie"
                      :series="chartData.collegeStatusSeries"
                    ></apexchart>
                  </v-card>
                </v-col>
                <v-col cols="12" md="7">
                  <v-card outlined class="rounded-lg pa-4 h-100">
                    <h3 class="text-subtitle-1 font-weight-bold mb-2">
                      สรุปงบประมาณแยกตามสถานะ
                    </h3>
                    <v-simple-table dense class="mt-4">
                      <thead>
                        <tr>
                          <th>สถานะ</th>
                          <th class="text-right">จำนวน</th>
                          <th class="text-right">งบจัดสรร</th>
                          <th class="text-right">เบิกจ่าย</th>
                          <th class="text-right">คงเหลือ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="s in budgetByStatus" :key="s.status">
                          <td>
                            <v-chip
                              x-small
                              :color="getStatusColor(s.status)"
                              dark
                              label
                              >{{ s.label }}</v-chip
                            >
                          </td>
                          <td class="text-right">{{ s.count }}</td>
                          <td class="text-right">
                            {{ formatCurrency(s.allocated) }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(s.disbursed) }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(s.remaining) }}
                          </td>
                        </tr>
                        <tr class="font-weight-bold grey lighten-4">
                          <td>รวม (ที่รายงาน)</td>
                          <td class="text-right">
                            {{ collegeStats.reported }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(budgetByStatusTotal.allocated) }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(budgetByStatusTotal.disbursed) }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(budgetByStatusTotal.remaining) }}
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>
                </v-col>
              </v-row>

              <!-- ตารางรายชื่อ -->
              <v-card class="rounded-lg" flat>
                <v-card-title class="flex-wrap">
                  <v-icon left color="primary">mdi-school</v-icon>
                  รายชื่อสถานศึกษาทั้งหมด
                  <v-spacer></v-spacer>

                  <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <v-btn-toggle
                      v-model="collegeFilter"
                      mandatory
                      dense
                      color="primary"
                    >
                      <v-btn small value="all">
                        ทั้งหมด ({{ filteredColleges.length }})
                      </v-btn>
                      <v-btn small value="reported" color="success">
                        รายงานแล้ว ({{ collegeStats.reported }})
                      </v-btn>
                      <v-btn small value="unreported" color="error">
                        ยังไม่รายงาน ({{ collegeStats.unreported }})
                      </v-btn>
                    </v-btn-toggle>

                    <v-text-field
                      v-model="searchCollege"
                      append-icon="mdi-magnify"
                      label="ค้นหาสถานศึกษา"
                      outlined
                      dense
                      rounded
                      hide-details
                      style="max-width: 280px;"
                    ></v-text-field>

                    <v-btn
                      color="success"
                      small
                      @click="exportCollegeTracking"
                      :loading="isExportingCollege"
                    >
                      <v-icon small left>mdi-microsoft-excel</v-icon>
                      ส่งออก
                    </v-btn>
                  </div>
                </v-card-title>

                <v-data-table
                  :headers="headers.collegeTable"
                  :items="displayColleges"
                  :search="searchCollege"
                  :items-per-page="20"
                  :loading="isLoading"
                  class="elevation-0"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 50, 100, -1],
                    'items-per-page-text': 'แสดง'
                  }"
                >
                  <template v-slot:item.user_status="{ item }">
                    <v-chip
                      x-small
                      label
                      :color="getUserStatusColor(item.user_status_raw)"
                      dark
                    >
                      {{ item.user_status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.college_name="{ item }">
                    <div class="font-weight-bold">{{ item.college_name }}</div>
                    <div class="text-caption grey--text">
                      {{ item.province_name }} | {{ item.region_name }}
                    </div>
                  </template>

                  <template v-slot:item.report_status="{ item }">
                    <v-chip
                      small
                      :color="
                        item.has_report
                          ? getStatusColor(item.report_status_raw)
                          : 'grey lighten-1'
                      "
                      :dark="item.has_report"
                      label
                    >
                      <v-icon left small v-if="item.has_report">
                        {{
                          item.report_status_raw === "approved"
                            ? "mdi-check"
                            : item.report_status_raw === "rejected"
                            ? "mdi-close"
                            : "mdi-clock"
                        }}
                      </v-icon>
                      <v-icon left small v-else
                        >mdi-minus-circle-outline</v-icon
                      >
                      {{ item.report_status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.allocated_budget="{ item }">
                    <span v-if="item.has_report">{{
                      formatCurrency(item.allocated_budget)
                    }}</span>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.disbursed_budget="{ item }">
                    <span v-if="item.has_report">{{
                      formatCurrency(item.disbursed_budget)
                    }}</span>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.remaining_budget="{ item }">
                    <span v-if="item.has_report">{{
                      formatCurrency(item.remaining_budget)
                    }}</span>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.letterFile="{ item }">
                    <v-btn
                      v-if="item.has_report && item.letterFile"
                      icon
                      small
                      color="primary"
                      @click="openFileViewer(item.letterFile)"
                      title="ดูหนังสือนำส่ง"
                    >
                      <v-icon>mdi-file-document-outline</v-icon>
                    </v-btn>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.report_file="{ item }">
                    <v-btn
                      v-if="item.has_report && item.report_file"
                      icon
                      small
                      color="success"
                      @click="openFileViewer(item.report_file)"
                      title="ดูไฟล์รายงาน"
                    >
                      <v-icon>mdi-file-chart-outline</v-icon>
                    </v-btn>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.disburse_pct="{ item }">
                    <template
                      v-if="item.has_report && item.allocated_budget > 0"
                    >
                      <v-progress-linear
                        :value="item.disburse_pct"
                        :color="getPercentageColor(item.disburse_pct)"
                        height="18"
                        rounded
                      >
                        <template v-slot:default="{ value }">
                          <strong class="white--text text-caption"
                            >{{ Math.ceil(value) }}%</strong
                          >
                        </template>
                      </v-progress-linear>
                    </template>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      v-if="item.has_report"
                      icon
                      small
                      color="primary"
                      @click="viewReport(item.report_data)"
                      title="ดูรายละเอียด"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="
                        item.has_report &&
                          ['submitted', 'checking'].includes(
                            item.report_status_raw
                          )
                      "
                      icon
                      small
                      color="success"
                      @click="confirmApprove(item.report_data)"
                      title="อนุมัติ"
                    >
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="
                        item.has_report &&
                          ['submitted', 'checking'].includes(
                            item.report_status_raw
                          )
                      "
                      icon
                      small
                      color="error"
                      @click="confirmReject(item.report_data)"
                      title="ปฏิเสธ/ตีกลับ"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="
                        item.has_report && item.report_status_raw === 'approved'
                      "
                      icon
                      small
                      color="warning"
                      @click="confirmCancel(item.report_data)"
                      title="ยกเลิกการอนุมัติ"
                    >
                      <v-icon>mdi-file-cancel</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="
                        item.has_report && item.report_status_raw === 'rejected'
                      "
                      icon
                      small
                      color="warning"
                      @click="confirmCancelRejected(item.report_data)"
                      title="ยกเลิกการปฏิเสธ/ตีกลับ"
                    >
                      <v-icon>mdi-file-cancel</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card>
            </v-container>
          </v-tab-item>

          <!-- ===== TAB 2: ตรวจสอบสถานะรายงาน ===== -->
          <v-tab-item>
            <v-container fluid class="pa-4">
              <v-row>
                <v-col cols="12" md="4">
                  <v-card outlined class="rounded-lg pa-4">
                    <h3 class="text-subtitle-1 font-weight-bold mb-2">
                      สัดส่วนสถานะการรายงาน
                    </h3>
                    <apexchart
                      type="donut"
                      height="250"
                      :options="chartOptions.statusPie"
                      :series="chartData.statusPieSeries"
                    ></apexchart>
                  </v-card>
                </v-col>
                <v-col cols="12" md="8">
                  <v-card outlined class="rounded-lg pa-4 h-100">
                    <h3 class="text-subtitle-1 font-weight-bold mb-2">
                      10 อันดับสถานศึกษาที่ส่งรายงานเร็วที่สุด
                    </h3>
                    <v-data-table
                      :headers="headers.topReport"
                      :items="filteredReports.slice(0, 10)"
                      hide-default-footer
                      dense
                    >
                      <template v-slot:item.status="{ item }">
                        <v-chip
                          x-small
                          :color="getStatusColor(item.status)"
                          dark
                          >{{ getStatusText(item.status) }}</v-chip
                        >
                      </template>
                    </v-data-table>
                  </v-card>
                </v-col>
              </v-row>

              <v-card class="mt-4 rounded-lg" flat>
                <v-card-title>
                  <v-icon left color="primary">mdi-format-list-checks</v-icon>
                  รายการรอตรวจสอบและอนุมัติ
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchReport"
                    append-icon="mdi-magnify"
                    label="ค้นหาชื่อสถานศึกษา/รหัส"
                    outlined
                    dense
                    rounded
                    hide-details
                    style="max-width: 300px;"
                  ></v-text-field>
                </v-card-title>
                <v-data-table
                  :headers="headers.reportTable"
                  :items="filteredReports"
                  :search="searchReport"
                  class="elevation-0"
                >
                  <template v-slot:item.college_name="{ item }">
                    <div class="font-weight-bold text-primary">
                      {{ item.college_name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.province_name }} | {{ item.region_name }}
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip
                      small
                      :color="getStatusColor(item.status)"
                      dark
                      label
                    >
                      <v-icon left small>{{
                        item.status === "approved"
                          ? "mdi-check"
                          : item.status === "rejected"
                          ? "mdi-close"
                          : "mdi-clock"
                      }}</v-icon>
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="viewReport(item)"
                      title="ดูรายละเอียด"
                      ><v-icon>mdi-eye</v-icon></v-btn
                    >
                    <v-btn
                      v-if="['submitted', 'checking'].includes(item.status)"
                      icon
                      small
                      color="success"
                      @click="confirmApprove(item)"
                      title="อนุมัติ"
                      ><v-icon>mdi-check-circle</v-icon></v-btn
                    >
                    <v-btn
                      v-if="['submitted', 'checking'].includes(item.status)"
                      icon
                      small
                      color="error"
                      @click="confirmReject(item)"
                      title="ปฏิเสธ"
                      ><v-icon>mdi-close-circle</v-icon></v-btn
                    >
                    <v-btn
                      v-if="item.status === 'approved'"
                      icon
                      small
                      color="warning"
                      @click="confirmCancel(item)"
                      title="ยกเลิก"
                      ><v-icon>mdi-file-cancel</v-icon></v-btn
                    >
                  </template>
                </v-data-table>
              </v-card>
            </v-container>
          </v-tab-item>

          <!-- ===== TAB 3: สถิติการเบิกจ่าย ===== -->
          <v-tab-item>
            <v-container fluid class="pa-4">
              <v-row>
                <v-col cols="12" md="8">
                  <v-card outlined class="rounded-lg pa-4">
                    <h3 class="text-h6 font-weight-bold mb-4">
                      กราฟเปรียบเทียบ จัดสรร vs เบิกจ่าย
                    </h3>
                    <apexchart
                      type="bar"
                      height="350"
                      :options="chartOptions.budgetBar"
                      :series="chartData.budgetBarSeries"
                    ></apexchart>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card outlined class="rounded-lg pa-4 h-100">
                    <h3 class="text-h6 font-weight-bold mb-4">
                      สัดส่วนการใช้งบประมาณ
                    </h3>
                    <div class="text-center my-6">
                      <v-progress-circular
                        :rotate="-90"
                        :size="180"
                        :width="25"
                        :value="summaryData.overall_disburse_percentage"
                        :color="
                          getPercentageColor(
                            summaryData.overall_disburse_percentage
                          )
                        "
                      >
                        <div class="text-center">
                          <div class="text-h4 font-weight-bold">
                            {{ summaryData.overall_disburse_percentage }}%
                          </div>
                          <div class="text-caption">ใช้งานไปแล้ว</div>
                        </div>
                      </v-progress-circular>
                    </div>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>งบจัดสรรรวม:</v-list-item-content>
                        <v-list-item-action class="font-weight-bold">{{
                          formatCurrency(summaryData.total_allocated)
                        }}</v-list-item-action>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>คงเหลือรวม:</v-list-item-content>
                        <v-list-item-action
                          class="font-weight-bold warning--text"
                          >{{
                            formatCurrency(summaryData.total_remaining)
                          }}</v-list-item-action
                        >
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>

              <v-card class="mt-4 rounded-lg" flat>
                <v-card-title>
                  <v-icon left color="success">mdi-table-large</v-icon>
                  รายละเอียดการเบิกจ่ายรายสถานศึกษา
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchBudget"
                    append-icon="mdi-magnify"
                    label="ค้นหาสถานศึกษา"
                    outlined
                    dense
                    rounded
                    hide-details
                    style="max-width: 300px;"
                  ></v-text-field>
                </v-card-title>
                <v-data-table
                  :headers="headers.budgetTable"
                  :items="filteredBudgets"
                  :search="searchBudget"
                >
                  <template v-slot:item.allocated_budget="{ item }">{{
                    formatCurrency(item.allocated_budget)
                  }}</template>
                  <template v-slot:item.disbursed_budget="{ item }">{{
                    formatCurrency(item.disbursed_budget)
                  }}</template>
                  <template v-slot:item.remaining_budget="{ item }">{{
                    formatCurrency(item.remaining_budget)
                  }}</template>
                  <template v-slot:item.disburse_percentage="{ item }">
                    <v-progress-linear
                      :value="item.disburse_percentage"
                      :color="getPercentageColor(item.disburse_percentage)"
                      height="20"
                      rounded
                    >
                      <template v-slot:default="{ value }"
                        ><strong class="white--text text-caption"
                          >{{ Math.ceil(value) }}%</strong
                        ></template
                      >
                    </v-progress-linear>
                  </template>
                </v-data-table>
              </v-card>
            </v-container>
          </v-tab-item>

          <!-- ===== TAB 4: พยากรณ์ ===== -->
          <v-tab-item>
            <v-container fluid class="pa-4">
              <v-alert type="info" text border="left" dense class="mb-4">
                <v-icon left>mdi-information</v-icon>
                การพยากรณ์คำนวณจากอัตราการเบิกจ่ายเฉลี่ย (Run Rate)
                เทียบกับระยะเวลาที่เหลือในงวด
              </v-alert>

              <v-row>
                <v-col cols="12">
                  <v-card outlined class="rounded-lg pa-4">
                    <h3 class="text-h6 font-weight-bold mb-4">
                      แนวโน้มและการพยากรณ์
                    </h3>
                    <apexchart
                      type="line"
                      height="350"
                      :options="chartOptions.trendLine"
                      :series="chartData.trendSeries"
                    ></apexchart>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col cols="12">
                  <v-card class="rounded-lg" flat>
                    <v-card-title class="red--text text--darken-2">
                      <v-icon left color="red darken-2"
                        >mdi-alert-octagon</v-icon
                      >
                      กลุ่มเสี่ยง: การเบิกจ่ายต่ำกว่าเกณฑ์
                    </v-card-title>
                    <v-card-text>
                      <v-data-table
                        :headers="headers.varianceTable"
                        :items="varianceData"
                        :sort-by="['variance']"
                        :sort-desc="[true]"
                      >
                        <template v-slot:item.disburse_percentage="{ item }">
                          <span class="error--text font-weight-bold"
                            >{{ item.disburse_percentage }}%</span
                          >
                        </template>
                        <template v-slot:item.variance="{ item }">
                          <v-chip
                            color="red lighten-4"
                            text-color="red darken-4"
                            small
                            label
                            >-{{ item.variance }}% จากเกณฑ์</v-chip
                          >
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-container>

    <!-- ===== Dialogs (unchanged logic) ===== -->
    <v-dialog v-model="dialogs.approve" max-width="500">
      <v-card class="rounded-lg">
        <v-card-title class="headline success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          ยืนยันการอนุมัติ
        </v-card-title>
        <v-card-text>
          คุณต้องการอนุมัติรายงานของ
          <strong>{{ selectedReport.college_name }}</strong> ใช่หรือไม่?
          <v-textarea
            v-model="actionNote"
            label="หมายเหตุ (ถ้ามี)"
            rows="2"
            class="mt-3"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.approve = false">ยกเลิก</v-btn>
          <v-btn color="success" @click="processApprove">ยืนยันอนุมัติ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.reject" max-width="500">
      <v-card class="rounded-lg">
        <v-card-title class="headline error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          ปฏิเสธรายงาน
        </v-card-title>
        <v-card-text>
          ระบุเหตุผลในการปฏิเสธรายงานของ
          <strong>{{ selectedReport.college_name }}</strong>
          <v-textarea
            v-model="actionNote"
            label="เหตุผล *"
            rows="3"
            class="mt-3"
            outlined
            :rules="[v => !!v || 'จำเป็นต้องระบุ']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.reject = false">ยกเลิก</v-btn>
          <v-btn color="error" @click="processReject" :disabled="!actionNote"
            >ยืนยันปฏิเสธ</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.cancel" max-width="500">
      <v-card class="rounded-lg">
        <v-card-title class="headline warning--text">
          <v-icon left color="warning">mdi-alert</v-icon>
          ยกเลิกการอนุมัติ
        </v-card-title>
        <v-card-text class="pt-4">
          คุณต้องการยกเลิกการอนุมัติรายงานของ
          <strong>{{ selectedReport.college_name }}</strong> ใช่หรือไม่?
          <br /><small class="grey--text"
            >สถานะจะถูกเปลี่ยนเป็น "รออนุมัติ" และต้องระบุเหตุผล</small
          >
          <v-textarea
            v-model="actionNote"
            label="เหตุผลในการยกเลิก *"
            rows="3"
            class="mt-3"
            outlined
            dense           
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.cancel = false">ปิด</v-btn>
          <v-btn color="warning" @click="processCancel" :disabled="!actionNote"
            >ยืนยันการยกเลิก</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.details" max-width="800">
      <v-card v-if="selectedReport" class="rounded-lg">
        <v-toolbar color="primary" dark flat>
          <v-toolbar-title>{{ selectedReport.college_name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogs.details = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="6"
              ><strong>รหัสสถานศึกษา:</strong>
              {{ selectedReport.college_code }}</v-col
            >
            <v-col cols="6"
              ><strong>วันที่รายงาน:</strong>
              {{ formatDate(selectedReport.created_at) }}</v-col
            >
            <v-col cols="6"
              ><strong>ผู้อำนวยการ:</strong>
              {{ selectedReport.director_name }}</v-col
            >
            <v-col cols="6"
              ><strong>สถานะ:</strong>
              {{ getStatusText(selectedReport.status) }}</v-col
            >
          </v-row>
          <v-divider class="my-4"></v-divider>
          <h3 class="mb-2">งบประมาณรายเดือน</h3>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>เดือน</th>
                <th>จัดสรร</th>
                <th>เบิกจ่าย</th>
                <th>คงเหลือ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in reportMonthlyDetails" :key="m.month">
                <td>{{ getMonthName(m.month) }}</td>
                <td>{{ formatCurrency(m.allocated_budget) }}</td>
                <td>{{ formatCurrency(m.disbursed_budget) }}</td>
                <td>{{ formatCurrency(m.remaining_budget) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewFilePdfDialog" max-width="80%">
      <v-card elevation="2" class="rounded-lg">
        <v-toolbar dense flat>
          <v-toolbar-title class="text-subtitle-1">
            <v-icon left>mdi-file-pdf-box</v-icon> ดูไฟล์แนบ
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            :href="
              'https://hrvec.ovec.go.th/HRvec_api/uploads/report/' +
                letter_reportFile
            "
            target="_blank"
            title="เปิดในแท็บใหม่"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon @click="viewFilePdfDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-toolbar>
        <embed
          :src="
            'https://hrvec.ovec.go.th/HRvec_api/uploads/report/' +
              letter_reportFile
          "
          width="100%"
          height="700px"
          type="application/pdf"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Manpowermenubar_govem from "../../../components/admin/manpowermenubar_govem.vue";
import * as XLSX from "xlsx";

export default {
  components: {
    Manpowermenubar_govem
  },

  data: () => ({
    ApiKey: "HRvec2021",
    activeTab: 0,
    isLoading: false,
    isExporting: false,
    isExportingCollege: false,
    searchReport: "",
    searchBudget: "",
    searchCollege: "",
    collegeFilter: "all",

    // Filters
    filters: {
      report_year: new Date().getFullYear() + 543,
      report_time: 1,
      selectedRegions: [],
      selectedProvinces: [],
      selectedUserStatuses: [] // ← เพิ่มใหม่: กรองตามกลุ่มสถานศึกษา
    },

    // User Status Options (กลุ่มสถานศึกษา)
    userStatusOptions: [
      { text: "สถานศึกษา", value: "B", icon: "mdi-school", color: "blue" },
      {
        text: "สถาบันการอาชีวศึกษา",
        value: "C",
        icon: "mdi-office-building",
        color: "purple"
      },
      { text: "สอจ.", value: "G", icon: "mdi-account-tie", color: "teal" },
      { text: "ศน.", value: "S", icon: "mdi-account-search", color: "orange" },
      { text: "ส่วนกลาง", value: "V", icon: "mdi-bank", color: "red" }
    ],

    // Master Data
    regions: [],
    provinces: [],
    users: [],
    yearOptions: [2567, 2568, 2569, 2570],
    timeOptions: [1, 2, 3, 4],

    // Data Store
    rawData: {
      reports: [],
      budgetAnalysis: [],
      trends: [],
      variance: []
    },

    // Summary Stats
    stats: {
      totalColleges: 0,
      approved: 0,
      submitted: 0,
      checking: 0,
      rejected: 0
    },

    summaryData: {
      total_allocated: 0,
      total_disbursed: 0,
      total_remaining: 0,
      overall_disburse_percentage: 0
    },

    // Dialog Control
    dialogs: {
      approve: false,
      reject: false,
      cancel: false,
      details: false
    },
    selectedReport: {},
    reportMonthlyDetails: [],
    actionNote: "",

    // Table Headers
    headers: {
      collegeTable: [
        { text: "กลุ่ม", value: "user_status", width: "120px" },
        { text: "รหัส", value: "college_code", width: "90px" },
        { text: "สถานศึกษา", value: "college_name" },
        { text: "สถานะรายงาน", value: "report_status", width: "140px" },
        {
          text: "งบจัดสรร",
          value: "allocated_budget",
          align: "right",
          width: "130px"
        },
        {
          text: "เบิกจ่าย",
          value: "disbursed_budget",
          align: "right",
          width: "130px"
        },
        {
          text: "คงเหลือ",
          value: "remaining_budget",
          align: "right",
          width: "130px"
        },
        { text: "% เบิกจ่าย", value: "disburse_pct", width: "120px" },
        {
          text: "หนังสือนำส่ง",
          value: "letterFile",
          width: "100px",
          align: "center"
        },
        {
          text: "ไฟล์รายงาน",
          value: "report_file",
          width: "100px",
          align: "center"
        },
        { text: "จัดการ", value: "actions", sortable: false, align: "right" }
      ],
      topReport: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "วันที่", value: "created_at" },
        { text: "สถานะ", value: "status" }
      ],
      reportTable: [
        { text: "รหัส", value: "college_code", width: "100px" },
        { text: "ชื่อสถานศึกษา", value: "college_name" },
        { text: "จำนวนพนักงาน", value: "total_employees", align: "center" },
        { text: "วันที่รายงาน", value: "created_at" },
        { text: "สถานะ", value: "status" },
        { text: "จัดการ", value: "actions", sortable: false, align: "right" }
      ],
      budgetTable: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "งบจัดสรร", value: "allocated_budget", align: "right" },
        { text: "เบิกจ่าย", value: "disbursed_budget", align: "right" },
        { text: "คงเหลือ", value: "remaining_budget", align: "right" },
        { text: "% เบิกจ่าย", value: "disburse_percentage", width: "150px" }
      ],
      varianceTable: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "เดือน", value: "month_name" },
        { text: "เบิกจ่ายจริง (%)", value: "disburse_percentage" },
        { text: "ส่วนต่าง (Variance)", value: "variance" }
      ]
    },

    // Chart Options
    chartOptions: {
      collegeStatusPie: {
        labels: [
          "รายงานแล้ว (อนุมัติ)",
          "รายงานแล้ว (รอตรวจ)",
          "รายงานแล้ว (ปฏิเสธ)",
          "ยังไม่รายงาน"
        ],
        colors: ["#4CAF50", "#03A9F4", "#F44336", "#BDBDBD"],
        legend: { position: "bottom" }
      },
      statusPie: {
        labels: ["อนุมัติ", "รอตรวจสอบ", "ปฏิเสธ", "ร่าง"],
        colors: ["#4CAF50", "#03A9F4", "#F44336", "#9E9E9E"],
        legend: { position: "bottom" }
      },
      budgetBar: {
        chart: { type: "bar", stacked: false },
        colors: ["#2196F3", "#4CAF50"],
        xaxis: { categories: [] },
        dataLabels: { enabled: false }
      },
      trendLine: {
        chart: { type: "line", toolbar: { show: true } },
        stroke: { curve: "smooth", dashArray: [0, 5] },
        colors: ["#1E88E5", "#FF9800"],
        xaxis: { categories: [] }
      }
    },
    chartData: {
      collegeStatusSeries: [],
      statusPieSeries: [],
      budgetBarSeries: [],
      trendSeries: []
    },
    viewFilePdfDialog: false,
    letter_reportFile: ""
  }),

  computed: {
    // ===== Select All Icon for User Status =====
    selectAllUserStatusIcon() {
      if (
        this.filters.selectedUserStatuses.length ===
        this.userStatusOptions.length
      )
        return "mdi-close-box";
      if (this.filters.selectedUserStatuses.length > 0) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },

    // ===== Active Filters Detection =====
    hasActiveFilters() {
      return (
        this.filters.selectedUserStatuses.length > 0 ||
        this.filters.selectedRegions.length > 0 ||
        this.filters.selectedProvinces.length > 0
      );
    },

    activeFilterChips() {
      const chips = [];
      if (this.filters.selectedUserStatuses.length > 0) {
        const labels = this.filters.selectedUserStatuses
          .map(v => this.userStatusOptions.find(o => o.value === v).text)
          .filter(Boolean);
        chips.push({ key: "userStatus", label: `กลุ่ม: ${labels.join(", ")}` });
      }
      if (this.filters.selectedRegions.length > 0) {
        const labels = this.filters.selectedRegions
          .map(v => this.regions.find(r => r.region_ID === v).region_name)
          .filter(Boolean);
        chips.push({ key: "regions", label: `ภาค: ${labels.join(", ")}` });
      }
      if (this.filters.selectedProvinces.length > 0) {
        const labels = this.filters.selectedProvinces
          .map(
            v => this.provinces.find(p => p.province_ID === v).province_name
          )
          .filter(Boolean);
        chips.push({
          key: "provinces",
          label: `จังหวัด: ${labels.join(", ")}`
        });
      }
      return chips;
    },

    // ===== User Status Summary (สรุปกลุ่มสถานศึกษา) =====
    userStatusSummary() {
      const colorMap = {
        B: "blue",
        C: "purple",
        G: "teal",
        S: "orange",
        V: "red"
      };
      const iconMap = {
        B: "mdi-school",
        C: "mdi-office-building",
        G: "mdi-account-tie",
        S: "mdi-account-search",
        V: "mdi-bank"
      };

      return this.userStatusOptions
        .map(opt => {
          const items = this.filteredColleges.filter(
            c => c.user_status_raw === opt.value
          );
          return {
            code: opt.value,
            label: opt.text,
            total: items.length,
            reported: items.filter(c => c.has_report).length,
            unreported: items.filter(c => !c.has_report).length,
            color: colorMap[opt.value] || "grey",
            icon: iconMap[opt.value] || "mdi-help"
          };
        })
        .filter(g => g.total > 0);
    },

    budgetByStatusTotal() {
      return this.budgetByStatus.reduce(
        (acc, s) => ({
          allocated: acc.allocated + s.allocated,
          disbursed: acc.disbursed + s.disbursed,
          remaining: acc.remaining + s.remaining
        }),
        { allocated: 0, disbursed: 0, remaining: 0 }
      );
    },

    availableProvinces() {
      if (!this.filters.selectedRegions.length) return this.provinces;
      return this.provinces.filter(p =>
        this.filters.selectedRegions.includes(p.region_ID)
      );
    },

    regionOptions() {
      return this.regions;
    },

    /**
     * สถานศึกษาทั้งหมด (กรองตามกลุ่ม/ภาค/จังหวัด)
     */
    filteredColleges() {
      return this.users
        .filter(u => {
          // กรองตามกลุ่มสถานศึกษา (user_status)
          const userStatusMatch =
            this.filters.selectedUserStatuses.length === 0 ||
            (u.user_status &&
              this.filters.selectedUserStatuses.includes(u.user_status));
          // กรองตามภาค
          const regionMatch =
            this.filters.selectedRegions.length === 0 ||
            (u.region_ID && this.filters.selectedRegions.includes(u.region_ID));
          // กรองตามจังหวัด
          const provinceMatch =
            this.filters.selectedProvinces.length === 0 ||
            (u.user_province_ID &&
              this.filters.selectedProvinces.includes(u.user_province_ID));
          return userStatusMatch && regionMatch && provinceMatch;
        })
        .map(u => {
          const report = this.rawData.reports.find(
            r => r.college_code === u.user_code
          );
          const hasReport = !!report;

          const allocated = hasReport
            ? parseFloat(report.allocated_budget) || 0
            : 0;
          const disbursed = hasReport
            ? parseFloat(report.disbursed_budget) || 0
            : 0;
          const remaining = hasReport
            ? parseFloat(report.remaining_budget) || 0
            : 0;
          const pct =
            hasReport && allocated > 0
              ? Math.round((disbursed / allocated) * 100)
              : 0;

          return {
            user_status: this.groupType(u.user_status),
            user_status_raw: u.user_status || "",
            college_code: u.user_code || "",
            college_name: u.college_name || u.agency || "",
            province_name: u.province_name || "",
            region_name: u.region_name || "",
            region_ID: u.region_ID,
            user_province_ID: u.user_province_ID,
            has_report: hasReport,
            report_status: hasReport
              ? this.getStatusText(report.status)
              : "ยังไม่รายงาน",
            report_status_raw: hasReport ? report.status : "",
            allocated_budget: allocated,
            disbursed_budget: disbursed,
            remaining_budget: remaining,
            disburse_pct: pct,
            letterFile: hasReport ? report.letterFile || "" : "",
            report_file: hasReport ? report.report_file || "" : "",
            report_data: report || null,
            created_at: hasReport ? report.created_at : ""
          };
        });
    },

    displayColleges() {
      if (this.collegeFilter === "reported")
        return this.filteredColleges.filter(c => c.has_report);
      if (this.collegeFilter === "unreported")
        return this.filteredColleges.filter(c => !c.has_report);
      return this.filteredColleges;
    },

    collegeStats() {
      const all = this.filteredColleges;
      const reported = all.filter(c => c.has_report).length;
      const unreported = all.length - reported;
      const pct =
        all.length > 0 ? Math.round((reported / all.length) * 100) : 0;
      return { reported, unreported, reportedPercentage: pct };
    },

    budgetByStatus() {
      const statusList = [
        { status: "approved", label: "อนุมัติแล้ว" },
        { status: "submitted", label: "รออนุมัติ" },
        { status: "checking", label: "กำลังตรวจสอบ" },
        { status: "rejected", label: "ปฏิเสธ" },
        { status: "draft", label: "ร่าง" },
        { status: "cancelled", label: "ยกเลิก" }
      ];

      return statusList
        .map(s => {
          const items = this.filteredColleges.filter(
            c => c.has_report && c.report_status_raw === s.status
          );
          return {
            ...s,
            count: items.length,
            allocated: items.reduce((sum, i) => sum + i.allocated_budget, 0),
            disbursed: items.reduce((sum, i) => sum + i.disbursed_budget, 0),
            remaining: items.reduce((sum, i) => sum + i.remaining_budget, 0)
          };
        })
        .filter(s => s.count > 0);
    },

    filteredReports() {
      return this.rawData.reports.filter(item => {
        // กรองตาม user_status (ต้องหา user ที่ตรงกัน)
        const userStatusMatch =
          this.filters.selectedUserStatuses.length === 0 ||
          (() => {
            const user = this.users.find(
              u => u.user_code === item.college_code
            );
            return (
              user &&
              this.filters.selectedUserStatuses.includes(user.user_status)
            );
          })();

        const regionMatch =
          this.filters.selectedRegions.length === 0 ||
          (item.region_ID &&
            this.filters.selectedRegions.includes(item.region_ID));
        const provinceMatch =
          this.filters.selectedProvinces.length === 0 ||
          (item.user_province_ID &&
            this.filters.selectedProvinces.includes(item.user_province_ID));
        return userStatusMatch && regionMatch && provinceMatch;
      });
    },

    filteredBudgets() {
      return this.rawData.budgetAnalysis.filter(budget => {
        const collegeInfo = this.rawData.reports.find(
          r => r.college_code === budget.college_code
        );
        if (!collegeInfo) return true;

        const userStatusMatch =
          this.filters.selectedUserStatuses.length === 0 ||
          (() => {
            const user = this.users.find(
              u => u.user_code === budget.college_code
            );
            return (
              user &&
              this.filters.selectedUserStatuses.includes(user.user_status)
            );
          })();

        const regionMatch =
          this.filters.selectedRegions.length === 0 ||
          (collegeInfo.region_ID &&
            this.filters.selectedRegions.includes(collegeInfo.region_ID));
        const provinceMatch =
          this.filters.selectedProvinces.length === 0 ||
          (collegeInfo.user_province_ID &&
            this.filters.selectedProvinces.includes(
              collegeInfo.user_province_ID
            ));
        return userStatusMatch && regionMatch && provinceMatch;
      });
    },

    varianceData() {
      return (this.rawData.variance || []).filter(v => {
        const collegeInfo = this.rawData.reports.find(
          r => r.college_code === v.college_code
        );
        if (!collegeInfo) return true;

        const userStatusMatch =
          this.filters.selectedUserStatuses.length === 0 ||
          (() => {
            const user = this.users.find(u => u.user_code === v.college_code);
            return (
              user &&
              this.filters.selectedUserStatuses.includes(user.user_status)
            );
          })();

        const regionMatch =
          this.filters.selectedRegions.length === 0 ||
          (collegeInfo.region_ID &&
            this.filters.selectedRegions.includes(collegeInfo.region_ID));
        const provinceMatch =
          this.filters.selectedProvinces.length === 0 ||
          (collegeInfo.user_province_ID &&
            this.filters.selectedProvinces.includes(
              collegeInfo.user_province_ID
            ));
        return userStatusMatch && regionMatch && provinceMatch;
      });
    }
  },

  watch: {
    filteredReports() {
      this.updateDashboardStats();
      this.updateStatusChart();
    },
    filteredBudgets() {
      this.updateBudgetChart();
    },
    filteredColleges() {
      this.updateCollegeStatusChart();
    }
  },

  async mounted() {
    await this.fetchMasterData();
    await this.fetchData();
  },

  methods: {
    // ===== Filter Management Methods =====
    toggleAllUserStatuses() {
      if (
        this.filters.selectedUserStatuses.length ===
        this.userStatusOptions.length
      ) {
        this.filters.selectedUserStatuses = [];
      } else {
        this.filters.selectedUserStatuses = this.userStatusOptions.map(
          o => o.value
        );
      }
    },

    removeUserStatus(val) {
      this.filters.selectedUserStatuses = this.filters.selectedUserStatuses.filter(
        v => v !== val
      );
    },

    removeRegion(val) {
      this.filters.selectedRegions = this.filters.selectedRegions.filter(
        v => v !== val
      );
      // ล้างจังหวัดที่ไม่อยู่ในภาคที่เลือก
      if (this.filters.selectedRegions.length > 0) {
        const validProvinces = this.availableProvinces.map(p => p.province_ID);
        this.filters.selectedProvinces = this.filters.selectedProvinces.filter(
          p => validProvinces.includes(p)
        );
      }
    },

    removeProvince(val) {
      this.filters.selectedProvinces = this.filters.selectedProvinces.filter(
        v => v !== val
      );
    },

    toggleUserStatusFilter(code) {
      const idx = this.filters.selectedUserStatuses.indexOf(code);
      if (idx >= 0) {
        this.filters.selectedUserStatuses.splice(idx, 1);
      } else {
        this.filters.selectedUserStatuses.push(code);
      }
    },

    clearFilter(key) {
      if (key === "userStatus") this.filters.selectedUserStatuses = [];
      if (key === "regions") {
        this.filters.selectedRegions = [];
        this.filters.selectedProvinces = [];
      }
      if (key === "provinces") this.filters.selectedProvinces = [];
    },

    resetFilters() {
      this.filters.selectedUserStatuses = [];
      this.filters.selectedRegions = [];
      this.filters.selectedProvinces = [];
    },

    // ===== File Viewer =====
    openFileViewer(fileName) {
      this.letter_reportFile = fileName;
      this.viewFilePdfDialog = true;
    },

    // ===== Data Fetching =====
    async fetchMasterData() {
      this.isLoading = true;
      try {
        const [provRes, regionRes, userRes] = await Promise.all([
          this.$http.post("province.php", { ApiKey: this.ApiKey }),
          this.$http.post("region.php", { ApiKey: this.ApiKey }),
          this.$http.post("user.php", {
            ApiKey: this.ApiKey,
            show_user_gov: "ok"
          })
        ]);
        this.provinces = provRes.data;
        this.regions = regionRes.data;
        this.users = Array.isArray(userRes.data) ? userRes.data : [];
      } catch (e) {
        console.error("Error fetching master data", e);
      }
      this.isLoading = false;
    },

    async fetchData() {
      this.isLoading = true;
      try {
        const payload = {
          ApiKey: this.ApiKey,
          report_year: this.filters.report_year,
          report_time: this.filters.report_time
        };

        const [
          reportsRes,
          budgetRes,
          trendRes,
          varianceRes,
          summaryRes
        ] = await Promise.all([
          this.$http.post("government_employee_report_complete.php", {
            ...payload,
            action: "get_reports"
          }),
          this.$http.post("government_employee_report_complete.php", {
            ...payload,
            action: "get_college_budget_analysis"
          }),
          this.$http.post("government_employee_report_complete.php", {
            ...payload,
            action: "get_budget_disbursement_trend"
          }),
          this.$http.post("government_employee_report_complete.php", {
            ...payload,
            action: "get_budget_variance_analysis",
            threshold: 15
          }),
          this.$http.post("government_employee_report_complete.php", {
            ...payload,
            action: "get_budget_summary"
          })
        ]);

        if (reportsRes.data.status) this.rawData.reports = reportsRes.data.data;
        if (budgetRes.data.status)
          this.rawData.budgetAnalysis = budgetRes.data.data;
        if (trendRes.data.status) this.rawData.trends = trendRes.data.data;
        if (varianceRes.data.status)
          this.rawData.variance = varianceRes.data.data;
        if (summaryRes.data.status) this.summaryData = summaryRes.data.data;

        this.updateDashboardStats();
        this.updateStatusChart();
        this.updateBudgetChart();
        this.updateTrendChart();
        this.updateCollegeStatusChart();
      } catch (error) {
        console.error("Fetch Data Error", error);
      } finally {
        this.isLoading = false;
      }
    },

    // ===== Chart & Stats Updates =====
    updateDashboardStats() {
      const reports = this.filteredReports;
      this.stats.approved = reports.filter(r => r.status === "approved").length;
      this.stats.submitted = reports.filter(
        r => r.status === "submitted"
      ).length;
      this.stats.checking = reports.filter(r => r.status === "checking").length;
      this.stats.rejected = reports.filter(r => r.status === "rejected").length;
      this.stats.totalColleges = reports.length;
    },

    updateStatusChart() {
      this.chartData.statusPieSeries = [
        this.stats.approved,
        this.stats.submitted + this.stats.checking,
        this.stats.rejected,
        this.filteredReports.filter(r => r.status === "draft").length
      ];
    },

    updateCollegeStatusChart() {
      const all = this.filteredColleges;
      const approved = all.filter(
        c => c.has_report && c.report_status_raw === "approved"
      ).length;
      const pending = all.filter(
        c =>
          c.has_report &&
          ["submitted", "checking", "draft"].includes(c.report_status_raw)
      ).length;
      const rejected = all.filter(
        c => c.has_report && c.report_status_raw === "rejected"
      ).length;
      const unreported = all.filter(c => !c.has_report).length;
      this.chartData.collegeStatusSeries = [
        approved,
        pending,
        rejected,
        unreported
      ];
    },

    updateBudgetChart() {
      const trends = this.rawData.trends;
      this.chartOptions.budgetBar = {
        ...this.chartOptions.budgetBar,
        xaxis: { categories: trends.map(t => t.month_name) }
      };
      this.chartData.budgetBarSeries = [
        { name: "งบจัดสรร", data: trends.map(t => t.total_allocated) },
        { name: "เบิกจ่ายจริง", data: trends.map(t => t.total_disbursed) }
      ];
    },

    updateTrendChart() {
      const trends = this.rawData.trends;
      const categories = trends.map(t => t.month_name);
      const actualData = trends.map(t => t.total_disbursed);

      if (trends.length > 0) {
        categories.push("คาดการณ์");
        actualData.push(null);
      }

      const forecastData = Array(trends.length).fill(null);
      if (trends.length > 0) {
        const lastVal = parseFloat(trends[trends.length - 1].total_disbursed);
        forecastData[trends.length - 1] = lastVal;
        forecastData[trends.length] = lastVal * 1.1;
      }

      this.chartOptions.trendLine = {
        ...this.chartOptions.trendLine,
        xaxis: { categories: categories }
      };
      this.chartData.trendSeries = [
        { name: "เบิกจ่ายจริง", type: "line", data: actualData },
        { name: "พยากรณ์", type: "line", data: forecastData }
      ];
    },

    // ===== Actions =====
    confirmCancel(item) {
      this.selectedReport = item;
      this.actionNote = "";
      this.dialogs.cancel = true;
    },

    confirmCancelRejected(item) {
      this.selectedReport = item;
      this.actionNote = "";
      this.dialogs.cancel = true;
    },

    async processCancel() {
      if (!this.actionNote) return;
      this.isLoading = true;
      try {
        const res = await this.$http.post(
          "government_employee_report_complete.php",
          {
            ApiKey: this.ApiKey,
            action: "cancel_approval",
            report_id: this.selectedReport.id,
            cancellation_reason: this.actionNote
          }
        );
        if (res.data.status) {
          this.dialogs.cancel = false;
          await this.fetchData();
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    viewReport(item) {
      this.selectedReport = item;
      this.$http
        .post("government_employee_report_complete.php", {
          ApiKey: this.ApiKey,
          action: "get_monthly_budget_details",
          report_id: item.id
        })
        .then(res => {
          if (res.data.status) {
            this.reportMonthlyDetails = res.data.data;
            this.dialogs.details = true;
          }
        });
    },

    confirmApprove(item) {
      this.selectedReport = item;
      this.actionNote = "";
      this.dialogs.approve = true;
    },

    async processApprove() {
      try {
        const res = await this.$http.post(
          "government_employee_report_complete.php",
          {
            ApiKey: this.ApiKey,
            action: "approve_report",
            report_id: this.selectedReport.id,
            notes: this.actionNote
          }
        );
        if (res.data.status) {
          this.dialogs.approve = false;
          this.fetchData();
        }
      } catch (e) {
        console.error(e);
      }
    },

    confirmReject(item) {
      this.selectedReport = item;
      this.actionNote = "";
      this.dialogs.reject = true;
    },

    async processReject() {
      try {
        const res = await this.$http.post(
          "government_employee_report_complete.php",
          {
            ApiKey: this.ApiKey,
            action: "reject_report",
            report_id: this.selectedReport.id,
            rejection_reason: this.actionNote
          }
        );
        if (res.data.status) {
          this.dialogs.reject = false;
          this.fetchData();
        }
      } catch (e) {
        console.error(e);
      }
    },

    // ===== Export =====
    exportCurrentView() {
      this.isExporting = true;
      let dataToExport = [];
      let sheetName = "Export";

      if (this.activeTab === 0) {
        sheetName = "College_Tracking";
        dataToExport = this.displayColleges.map(c => ({
          กลุ่ม: c.user_status,
          รหัสสถานศึกษา: c.college_code,
          ชื่อสถานศึกษา: c.college_name,
          จังหวัด: c.province_name,
          ภาค: c.region_name,
          สถานะรายงาน: c.report_status,
          งบจัดสรร: c.allocated_budget,
          เบิกจ่าย: c.disbursed_budget,
          คงเหลือ: c.remaining_budget,
          "% เบิกจ่าย": c.disburse_pct
        }));
      } else if (this.activeTab === 1) {
        sheetName = "Report_Status";
        dataToExport = this.filteredReports.map(r => ({
          รหัส: r.college_code,
          ชื่อสถานศึกษา: r.college_name,
          ภาค: r.region_name,
          จังหวัด: r.province_name,
          สถานะ: this.getStatusText(r.status),
          จำนวนพนักงาน: r.total_employees,
          วันที่รายงาน: r.created_at
        }));
      } else {
        sheetName = "Budget_Stats";
        dataToExport = this.filteredBudgets.map(b => ({
          รหัส: b.college_code,
          ชื่อสถานศึกษา: b.college_name,
          งบจัดสรร: b.allocated_budget,
          เบิกจ่าย: b.disbursed_budget,
          คงเหลือ: b.remaining_budget,
          "% เบิกจ่าย": b.disburse_percentage + "%"
        }));
      }

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(
        wb,
        `${sheetName}_${this.filters.report_year}_งวด${this.filters.report_time}.xlsx`
      );
      this.isExporting = false;
    },

    exportCollegeTracking() {
      this.isExportingCollege = true;
      try {
        const allData = this.displayColleges.map((c, idx) => ({
          ลำดับ: idx + 1,
          กลุ่ม: c.user_status,
          รหัสสถานศึกษา: c.college_code,
          ชื่อสถานศึกษา: c.college_name,
          จังหวัด: c.province_name,
          ภาค: c.region_name,
          สถานะรายงาน: c.report_status,
          "งบจัดสรร (บาท)": c.allocated_budget || 0,
          "เบิกจ่าย (บาท)": c.disbursed_budget || 0,
          "คงเหลือ (บาท)": c.remaining_budget || 0,
          "อัตราเบิกจ่าย (%)": c.disburse_pct || 0
        }));

        const summaryByStatus = this.budgetByStatus.map(s => ({
          สถานะ: s.label,
          จำนวนสถานศึกษา: s.count,
          "งบจัดสรร (บาท)": s.allocated,
          "เบิกจ่าย (บาท)": s.disbursed,
          "คงเหลือ (บาท)": s.remaining
        }));
        summaryByStatus.push({
          สถานะ: "ยังไม่รายงาน",
          จำนวนสถานศึกษา: this.collegeStats.unreported,
          "งบจัดสรร (บาท)": 0,
          "เบิกจ่าย (บาท)": 0,
          "คงเหลือ (บาท)": 0
        });
        summaryByStatus.push({
          สถานะ: "รวมทั้งหมด",
          จำนวนสถานศึกษา: this.filteredColleges.length,
          "งบจัดสรร (บาท)": this.summaryData.total_allocated || 0,
          "เบิกจ่าย (บาท)": this.summaryData.total_disbursed || 0,
          "คงเหลือ (บาท)": this.summaryData.total_remaining || 0
        });

        const unreportedData = this.filteredColleges
          .filter(c => !c.has_report)
          .map((c, idx) => ({
            ลำดับ: idx + 1,
            กลุ่ม: c.user_status,
            รหัสสถานศึกษา: c.college_code,
            ชื่อสถานศึกษา: c.college_name,
            จังหวัด: c.province_name,
            ภาค: c.region_name
          }));

        // Sheet สรุปตามกลุ่ม
        const groupSummaryData = this.userStatusSummary.map(g => ({
          กลุ่ม: g.label,
          จำนวนทั้งหมด: g.total,
          รายงานแล้ว: g.reported,
          ยังไม่รายงาน: g.unreported,
          "อัตราการรายงาน (%)":
            g.total > 0 ? Math.round((g.reported / g.total) * 100) : 0
        }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(allData),
          "รายชื่อทั้งหมด"
        );
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(summaryByStatus),
          "สรุปตามสถานะ"
        );
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(groupSummaryData),
          "สรุปตามกลุ่ม"
        );
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(unreportedData),
          "ยังไม่รายงาน"
        );
        XLSX.writeFile(
          wb,
          `ติดตามสถานศึกษา_${this.filters.report_year}_งวด${this.filters.report_time}.xlsx`
        );
      } catch (e) {
        console.error("Export error:", e);
      } finally {
        this.isExportingCollege = false;
      }
    },

    // ===== Helpers =====
    formatCurrency(value) {
      return new Intl.NumberFormat("th-TH", {
        style: "currency",
        currency: "THB"
      }).format(value || 0);
    },
    formatCurrencyShort(value) {
      const num = parseFloat(value) || 0;
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + " พันล้าน";
      if (num >= 1000000) return (num / 1000000).toFixed(1) + " ล้าน";
      if (num >= 1000) return (num / 1000).toFixed(0) + " พัน";
      return num.toFixed(0);
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("th-TH");
    },
    getStatusColor(status) {
      const map = {
        approved: "success",
        submitted: "info",
        checking: "warning",
        rejected: "error",
        cancelled: "orange",
        draft: "grey"
      };
      return map[status] || "grey";
    },
    getStatusText(status) {
      const map = {
        approved: "อนุมัติแล้ว",
        submitted: "รออนุมัติ",
        checking: "กำลังตรวจสอบ",
        rejected: "ปฏิเสธ",
        cancelled: "ยกเลิก",
        draft: "ร่าง"
      };
      return map[status] || status;
    },
    getUserStatusColor(code) {
      const map = { B: "blue", C: "purple", G: "teal", S: "orange", V: "red" };
      return map[code] || "grey";
    },
    getPercentageColor(val) {
      if (val > 80) return "success";
      if (val > 50) return "warning";
      return "error";
    },
    getMonthName(m) {
      const months = [
        "",
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม"
      ];
      return months[m] || m;
    },
    groupType(user_status) {
      const groupMap = {
        B: "สถานศึกษา",
        C: "สถาบันการอาชีวศึกษา",
        G: "สอจ.",
        S: "ศน.",
        V: "ส่วนกลาง"
      };
      return groupMap[user_status] || "ไม่ทราบกลุ่ม";
    }
  }
};
</script>

<style scoped>
/* ===== App Background ===== */
.app-bg {
  background: linear-gradient(135deg, #f0f4f8 0%, #e8edf2 100%);
  min-height: 100vh;
}

/* ===== Header Card ===== */
.header-card {
  overflow: hidden;
}
.header-title-bar {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 50%, #1a237e 100%);
}

/* ===== Filter Section ===== */
.filter-section {
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
  border-top: 1px solid #e3e8ee;
}
.filter-input .v-input__slot {
  border-radius: 8px !important;
}

/* ===== Summary Cards ===== */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  position: relative;
}
.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
}
.summary-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}
.summary-card--colleges::before {
  background: linear-gradient(90deg, #1976d2, #42a5f5);
}
.summary-card--allocated::before {
  background: linear-gradient(90deg, #2e7d32, #66bb6a);
}
.summary-card--disbursed::before {
  background: linear-gradient(90deg, #ef6c00, #ffa726);
}
.summary-card--pending::before {
  background: linear-gradient(90deg, #00838f, #26c6da);
}

.summary-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.summary-icon--blue {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
}
.summary-icon--green {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
}
.summary-icon--orange {
  background: linear-gradient(135deg, #ef6c00, #ffa726);
}
.summary-icon--cyan {
  background: linear-gradient(135deg, #00838f, #26c6da);
}

/* ===== Group Stat Cards ===== */
.group-stat-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent !important;
}
.group-stat-card:hover {
  background: #f5f5f5;
  transform: translateY(-1px);
}
.group-stat-card--active {
  border-color: #1976d2 !important;
  background: #e3f2fd !important;
}

/* ===== General ===== */
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.h-100 {
  height: 100%;
}
</style>
